import type { User, UserWRole } from "@repo/types";
import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "@repo/ui/components/Base/Input";
import { Button } from "@repo/ui/components/Base/Button";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { capitalize } from "@repo/shared/utils";
import { Label } from "@repo/ui/components/Base/Label";
import { AddressFormField } from "./AddressFormField";
import { ROLES } from "@/lib/constants";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { RoleDefinitionsDialog } from "../common/RoleDefinitionsDialog";
import { MultiPhoneFormField } from "./MultiPhoneFormField";
import { useState } from "react";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { apiService } from "@/services/apiService";
import { useGetAccountByEmail } from "@/hooks/queryHooks/accountHooks";
import { userRepo } from "@/repositories/userRepo";
import { tenantRepo } from "@/repositories/tenantRepo";

interface EditUserFormProps {
  user: UserWRole | User;
  tenantId: string;
  handleCancel: () => void;
  handleComplete: () => void;
}
export const EditUserForm = ({
  user,
  tenantId,
  handleCancel,
  handleComplete,
}: EditUserFormProps) => {
  useBlockUnload();

  const validationSchema = yup.object({
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
  });

  // Try to find an account with this email. If not found, user hasn't signed up.
  const { data: account, loaded: isAccountLoaded } = useGetAccountByEmail(
    user.email
  );
  const userSignedUp = !isAccountLoaded || !!account;

  const [reinviteLoading, setReinviteLoading] = useState(false);

  const handleReinvite = async () => {
    setReinviteLoading(true);
    await apiService.v1.mailNewUser({ tenantId, email: user.email });
    setReinviteLoading(false);
  };

  const hasRole = (obj: User | UserWRole): obj is UserWRole => {
    return "role" in obj;
  };

  return (
    <Formik
      initialValues={{
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        primaryAddress: user.primaryAddress,
        primaryPhone: user.primaryPhone,
        role: hasRole(user) ? user.role : undefined,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        if (!tenantId) {
          console.log("No tenant ID", tenantId);
          return;
        }

        const { role: _, ...valuesNoRole } = values;
        await userRepo.update(user.id, valuesNoRole);
        if (hasRole(user)) {
          await tenantRepo.addUser(tenantId, user.id, values.role ?? "view");
        }

        handleComplete();
      }}
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        errors,
        touched,
        isSubmitting,
      }) => (
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-y-4">
            <p className="text-balance">
              <strong>Notice:</strong> This information is intended for the
              public. Do not include personal details that should remain
              confidential.
            </p>
            <Label label={"Email: " + values.email} />
            <Input
              id="firstName"
              label="*First Name"
              name="firstName"
              disabled={isSubmitting}
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.firstName && touched.firstName}
              errorMessage={errors.firstName}
            />
            <Input
              id="lastName"
              label="*Last Name"
              name="lastName"
              disabled={isSubmitting}
              value={values.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.lastName && touched.lastName}
              errorMessage={errors.lastName}
            />

            {hasRole(user) && (
              <div className="flex flex-row gap-x-2">
                <SelectInput
                  label="*Role"
                  disabled={isSubmitting}
                  containerStyle="flex-grow"
                  onValueChange={(value) => setFieldValue("role", value)}
                  value={values.role || "submit"}
                  placeholder="Select a user role"
                  options={ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {capitalize(role)}
                    </SelectItem>
                  ))}
                />
                <RoleDefinitionsDialog />
              </div>
            )}

            <Label label="Primary Address" />
            <AddressFormField
              disabled={isSubmitting}
              fieldName="primaryAddress"
              address={values.primaryAddress}
              setFieldValue={setFieldValue}
            />

            <Label label="Phone Number" />
            <MultiPhoneFormField
              disabled={isSubmitting}
              fieldName="primaryPhone"
              phoneNumbers={values.primaryPhone}
              setFieldValue={setFieldValue}
            />

            <div className="flex flex-row justify-between items-center gap-x-4">
              <Button
                variant="destructive"
                disabled={isSubmitting}
                onClick={() => {
                  handleCancel();
                }}
              >
                Cancel
              </Button>
              {/* <div className="flex-grow" /> */}
              {!isAccountLoaded ? (
                <p>Checking user sign-up status...</p>
              ) : userSignedUp ? (
                <p>User has signed up.</p>
              ) : (
                <div className="flex flex-row items-center gap-x-4">
                  <p>Notice: User has not signed up yet.</p>
                  <Button
                    disabled={isSubmitting || reinviteLoading}
                    onClick={() => {
                      handleReinvite();
                    }}
                  >
                    Re-send Invite
                  </Button>
                </div>
              )}
              <Button type="submit" disabled={isSubmitting}>
                Save
              </Button>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};
